// ═══════════════════════════════════════════════════════════════
// ScheduleVisitModal — book a trip out to a job that doesn't have
// one on the calendar.
//
// The case this exists for: a customer approves an estimate at 9pm
// from their couch. The job is approved, the work isn't done, and
// nothing is on anyone's calendar. Before this, the tech had to
// notice that on their own.
//
// Writes the same returnVisits[] entry the Leave flow does (shared
// via appendReturnVisitPatch) so the calendar can't tell which
// screen booked it.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import { X, CalendarPlus, Package, AlertTriangle } from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import {
  toDate, flatMapVisits, appendReturnVisitPatch, formatPrice,
} from './lib/job-helpers.js';

const DEFAULT_DURATION = 60;

function pad2(n) { return String(n).padStart(2, '0'); }
function dateInputValue(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function timeInputValue(d) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function combineDateAndTime(dateStr, timeStr) {
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  const [hh, mm] = (timeStr || '').split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
}
function formatTimeOfDay(d) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// Default to tomorrow morning — approvals mostly land in the evening,
// and "tomorrow at 9" is the answer far more often than any other.
function defaultDateTime() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

export default function ScheduleVisitModal({ job, customer, allJobs = [], onClose, onScheduled }) {
  const initial = useMemo(defaultDateTime, []);
  const [date, setDate] = useState(dateInputValue(initial));
  const [time, setTime] = useState(timeInputValue(initial));
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [notes, setNotes] = useState('');
  const [waitingPart, setWaitingPart] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Live overlap warning against every other visit on the calendar.
  const conflict = useMemo(() => {
    const start = combineDateAndTime(date, time);
    const dur = Number(duration);
    if (isNaN(start.getTime()) || !isFinite(dur) || dur < 1) return null;
    const end = new Date(start.getTime() + dur * 60_000);
    return flatMapVisits(allJobs).find((b) => {
      if (b.id === job.id) return false;   // this job's own visits don't count
      const bEnd = new Date(b._start.getTime() + b._durationMinutes * 60_000);
      return b._start < end && start < bEnd;
    }) || null;
  }, [allJobs, date, time, duration, job.id]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const dur = Number(duration);
    if (!isFinite(dur) || dur < 15) {
      setError('Duration must be at least 15 minutes.');
      return;
    }
    const scheduledAt = combineDateAndTime(date, time);
    if (isNaN(scheduledAt.getTime())) {
      setError('Pick a valid date and time.');
      return;
    }

    setSubmitting(true);
    try {
      const { patch } = appendReturnVisitPatch(job, {
        scheduledAt,
        durationMinutes: dur,
        notes,
        waitingPart,
      });
      await updateOne(jobsCol(), job.id, patch);
      onScheduled?.(scheduledAt);
      onClose();
    } catch (err) {
      console.error('Schedule visit failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 style={{ flex: 1, margin: 0 }}>Schedule the work</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="schedule-job-summary">
          <strong>{customer?.name || 'Customer'}</strong> approved
          {Number(job.totalPrice) > 0 && <> · {formatPrice(job.totalPrice)}</>}
          <span className="muted small">{job.description}</span>
        </div>

        <form className="form" onSubmit={handleSubmit}>
          <div className="form-row">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="form-row">
            <span>Time</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
          </div>
          <div className="form-row">
            <span>Minutes</span>
            <input
              type="number" min="15" step="15" inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
            />
          </div>

          <label className="schedule-part-toggle">
            <input
              type="checkbox"
              checked={waitingPart}
              onChange={(e) => setWaitingPart(e.target.checked)}
            />
            <Package size={15} />
            <span>Waiting on a part to come in</span>
          </label>

          <div className="form-row">
            <span>Notes</span>
            <textarea
              rows={2}
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything you need to remember for this trip…"
            />
          </div>

          {conflict && (
            <p className="schedule-conflict">
              <AlertTriangle size={14} />
              Overlaps another visit at {formatTimeOfDay(conflict._start)}
              {' — '}you can still book it.
            </p>
          )}

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={submitting}>
              <CalendarPlus size={16} strokeWidth={2.5} />
              {submitting ? 'Scheduling…' : 'Put it on the calendar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
